import type { ComponentType, LazyExoticComponent, PropsWithoutRef } from 'react';
import React, { Suspense, lazy } from 'react';

import { isChunkLoadError } from './isChunkLoadError';
import type { LoadOnRenderOptions } from './loadOnRender.types';
import { retryImport } from './retryImport';

/**
 * Upper bound on how many times a failed chunk load is re-attempted by recreating the lazy
 * component. This caps the network cost of a permanently-unavailable chunk (e.g. when a consumer
 * error boundary re-renders its children on catch) while still letting transient failures
 * self-heal. The budget is reset after any successful load, so only consecutive failures count
 * against it; once exhausted the loader degrades to React.lazy's fail-fast cached rejection.
 */
export const MAX_CHUNK_LOAD_RECREATIONS = 10;

/**
 * This is a route-based lazy-loader function. It returns a component which lazy-loads a component
 * as a separate JavaScript bundle when rendered. It will show the "loading" component using React.Suspense
 * while waiting for the bundle to load.
 *
 * Chunk loads are made resilient in two ways:
 * - `retryImport` retries transient chunk-load failures (a momentary CDN/network blip) with backoff
 *   before the rejection ever reaches React.lazy.
 * - If a load still fails, the lazy component is recreated so the *next* mount re-attempts a fresh
 *   import. React.lazy permanently caches a rejected import, so without this reset a single blip
 *   wedges every future render of the component for the life of the JS realm — which is what inflates
 *   `ChunkLoadError` telemetry in long-lived hosts (e.g. Outlook re-logging the same wedge on every
 *   re-open). Successful loads keep a stable lazy component, so the happy path is unchanged.
 *   Recreations are bounded by `MAX_CHUNK_LOAD_RECREATIONS` (reset on success) so a permanently
 *   unavailable chunk degrades to fail-fast rather than re-attempting the network forever.
 */
export default function loadOnRender<T extends object>({
  loader,
  loading: Loading,
  fallback,
}: LoadOnRenderOptions<T>): ComponentType<PropsWithoutRef<T>> {
  const placeholder = fallback ?? (Loading ? <Loading /> : null);

  let LazyLoadedComponent: LazyExoticComponent<ComponentType<T>>;
  let consecutiveRecreations = 0;

  const createLazyComponent = (): LazyExoticComponent<ComponentType<T>> =>
    lazy(async () => {
      try {
        const componentModule = await retryImport(loader);
        consecutiveRecreations = 0;

        return componentModule;
      } catch (error) {
        if (isChunkLoadError(error) && consecutiveRecreations < MAX_CHUNK_LOAD_RECREATIONS) {
          consecutiveRecreations += 1;
          LazyLoadedComponent = createLazyComponent();
        }

        throw error;
      }
    });

  LazyLoadedComponent = createLazyComponent();

  return (props) => (
    <Suspense fallback={placeholder}>
      <LazyLoadedComponent {...props} />
    </Suspense>
  );
}
